<template>
  <Pane :value="paneIsOpen"
        :tabs="tabs"
        current-tab="filter"
        location="left"
        temporary
        data-locator="logs-filters-pane"
        @closeButtonClicked="paneIsOpen = false"
        @update:modelValue="onPaneValueChange"
  >
    <template #content-filter>
      <div class="text-body-1 font-weight-bold text-text-primary mb-2">
        {{ $t('project.Logs.LogsFiltersPane.dates') }}
      </div>
      <AppDateRangePicker v-model="filters.date" />

      <v-divider class="my-6" />

      <div class="text-body-1 font-weight-bold text-text-primary mb-2">
        {{ $t('project.Logs.LogsFiltersPane.users') }}
      </div>
      <DocumentSharePicker :force-pm-selection="false" />

      <v-divider class="my-6" />

      <template v-if="logsStore.logsTypes">
        <div class="font-weight-bold text-body-1 text-text-primary mb-2">
          {{ $t('project.Logs.LogsFiltersPane.logsTypes') }}
        </div>

        <div class="logs-types-container">
          <v-checkbox v-for="(value, key, index) in logsStore.logsTypes"
                      :key="`log-type-${index}`"
                      v-model="filters.types"
                      :value="key"
                      density="compact"
                      hide-details
          >
            <template #label>
              <span class="text-capitalize-first-letter">
                {{ value.replaceAll('_', ' ').toLowerCase() }}
              </span>
            </template>
          </v-checkbox>
        </div>

        <v-divider class="my-6" />
      </template>

      <v-row>
        <v-col>
          <div class="d-flex justify-end ga-4">
            <app-button
              type="outlined"
              variant="neutral"
              data-locator="clear-button"
              @click="onClearAllFilters"
            >
              {{$t('common.clear')}}
            </app-button>
            <app-button
              data-locator="apply-button"
              @click="onValidateClick"
            >
              {{$t('common.apply')}}
            </app-button>
          </div>
        </v-col>
      </v-row>
    </template>
  </Pane>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'

import AppDateRangePicker from '@/common/AppDateRangePicker.vue'
import DocumentSharePicker from '@/common/document-share/DocumentSharePicker.vue'
import Pane from '@/common/Pane.vue'
import { ISOToShortenedDate } from '@/common/utils/dates'
import { Filter } from '@/models/filter.model'
import { DocumentSharePickerActionTypes } from '@/store/modules/document-share-picker/action_types'
import { useLogsStore } from '@/stores/logsStore'

export default {
  name: 'LogsFiltersPane',
  components: {
    AppDateRangePicker,
    DocumentSharePicker,
    Pane,
  },
  setup() {
    const logsStore = useLogsStore()
    return { logsStore }
  },
  data () {
    return {
      tabs: [{ id: 'filter', title: this.$t('project.Logs.LogsFiltersPane.filters') }],
      paneIsOpen: false,
      filters: {
        date: [],
        types: [],
      },
    }
  },
  computed: {
    ...mapState('documentSharePicker', ['selectedMembers', 'selectedGroups']),
    ...mapState('groups', ['groups']),
    ...mapGetters('groups', ['allRoomMembers']),
    ...mapGetters('room', ['roomMnemo']),
  },
  watch: {
    'logsStore.logsFiltersPaneIsOpen': {
      handler (value) {
        if (value) {
          this.syncAppliedFiltersWithLocals()
          setTimeout(() => { this.paneIsOpen = true }, 300)
        }
      },
      immediate: true,
    },
    paneIsOpen (value) {
      if (!value) {
        setTimeout(() => {
          this.logsStore.logsFiltersPaneIsOpen = false
        }, 300)
      }
    },
  },
  created () {
    if (!this.logsStore.logsTypes) {
      this.prepareGetLogsTypes()
    }
  },
  methods: {
    ...mapActions('documentSharePicker', [
      DocumentSharePickerActionTypes.INIT_SELECTED_GROUPS,
      DocumentSharePickerActionTypes.INIT_SELECTED_MEMBERS,
      DocumentSharePickerActionTypes.RESET_SELECTED_GROUPS_AND_MEMBERS,
    ]),
    applyFilters () {
      const logDateFilter = this.filters.date.length > 0
        ? [
          new Filter(
            'date',
            this.$t('project.Logs.LogsFiltersPane.dates'),
            this.filters.date,
            `${ISOToShortenedDate(this.filters.date[0])} - ${ISOToShortenedDate(this.filters.date[1])}`,
          )]
        : []

      const usersFilters = this.selectedMembers.map(member =>
        new Filter('users', this.$t('common.user'), member.id, member.fullName),
      )

      const groupsFilters = this.selectedGroups.map(group =>
        new Filter('groups', this.$t('common.group'), group.id, group.name),
      )

      const typesFilter = this.filters.types.map(type => {
        let typeName = this.logsStore.logsTypes[type].replaceAll('_', ' ').toLowerCase()
        typeName = typeName.charAt(0).toUpperCase() + typeName.slice(1)
        return new Filter('types', this.$t('common.type'), type, typeName)
      })

      const filters = [
        ...logDateFilter,
        ...usersFilters,
        ...groupsFilters,
        ...typesFilter,
      ]

      this.logsStore.logsFilters = filters
    },
    syncAppliedFiltersWithLocals () {
      const dateFilter = this.logsStore.logsFilters.find(filter => filter.category.key === 'date')
      this.filters.date = dateFilter?.value.key ?? []

      const userFilters = this.logsStore.logsFilters.filter(filter => filter.category.key === 'users')
      const usersWithMatchingFilter = this.allRoomMembers.filter(
        member => userFilters.some(userFilter => userFilter.value.key === member.id),
      )
      this.INIT_SELECTED_MEMBERS(usersWithMatchingFilter)

      const groupFilters = this.logsStore.logsFilters.filter(filter => filter.category.key === 'groups')
      const groupsWithMatchingFilter = this.groups.filter(
        group => groupFilters.some(groupFilter => groupFilter.value.key === group.id),
      )
      this.INIT_SELECTED_GROUPS(groupsWithMatchingFilter)

      const typesFilter = this.logsStore.logsFilters.filter(filter => filter.category.key === 'types')
      this.filters.types = typesFilter.map(type => type.value.key) || []
    },
    onClearAllFilters () {
      this.RESET_SELECTED_GROUPS_AND_MEMBERS()
      this.logsStore.logsFilters = []
    },
    onValidateClick () {
      this.applyFilters()
      this.paneIsOpen = false
    },
    onPaneValueChange (value) {
      if (!value) {
        this.paneIsOpen = false
      }
    },
    async prepareGetLogsTypes () {
      await this.logsStore.getLogsTypes(this.roomMnemo)
    },
  },
}
</script>

<style scoped lang="scss">
.logs-types-container {
  max-height: 300px;
  overflow-y: auto;
}
</style>
